import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useStateContext } from "contexts/ContextProvider";
import CardDashboardText from "components/CardDashboardText";
// MUI components
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { Box, useTheme, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CardDashboardItem from "components/CardDashboardItem";
import { formatDateLong } from "helpers/ServiceHelpers";
import api from "services/api";
import dayjs from "dayjs";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import useDataGridScrollRestore from "hooks/useDataGridScrollRestore";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";
import ClearIcon from "@mui/icons-material/ClearRounded";
import { GridToolbarContainer } from "@mui/x-data-grid";
import CEQuickFilter from "components/CEQuickFilter";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import { OVERLAY_NAMES } from "constants/Overlays";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";

// Helper function to convert month name to number (1-12)
const getMonthNumber = (monthName) => {
  const months = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
  };
  return months[monthName] || null;
};

const DashboardInterpretationsMonthly = ({ user, dateSelection }) => {
  // Default values in case dateSelection is incomplete
  const defaultMonth = dayjs().month() + 1; // Current month (1-12)
  const defaultYear = dayjs().year(); // Current year

  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRow, setSelectedRow] = React.useState();

  // Convert month names to numbers if needed - memoized to prevent callback recreation
  const monthNumbers = useMemo(
    () =>
      Array.isArray(dateSelection?.months) && dateSelection.months.length > 0
        ? dateSelection.months.map((m) => {
            // Try to convert to number if it's a string month name
            if (isNaN(m)) {
              return getMonthNumber(m) || defaultMonth;
            }
            return Number(m);
          })
        : [defaultMonth],
    [dateSelection?.months, defaultMonth],
  );

  // Get the year from dateSelection or use default - memoized to prevent callback recreation
  const year = useMemo(
    () => (dateSelection?.year ? Number(dateSelection.year) : defaultYear),
    [dateSelection?.year, defaultYear],
  );

  // Get the name of the first month for display
  const thisMonth = {
    long: dayjs()
      .month(monthNumbers[0] - 1)
      .format("MMMM"),
    short: monthNumbers[0],
  };

  const { refreshPage, showSnackbar } = useStateContext();
  const theme = useTheme();

  const { setCurrentEntity } = usePresence();
  usePagePresence();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const {
    handleErrorDialog,
    setOverlayData,
    setOverlayName,
  } = useStateContext();

  const [requestedAssignments, setRequestedAssignments] = useState();
  const [confirmedAssignments, setConfirmedAssignments] = useState();

  // Preserve scroll positions across refetches (saves bounce to the top)
  const requestedApiRef = useGridApiRef();
  const { capture: captureRequestedScroll, restore: restoreRequestedScroll } =
    useDataGridScrollRestore(requestedApiRef);
  const confirmedApiRef = useGridApiRef();
  const { capture: captureConfirmedScroll, restore: restoreConfirmedScroll } =
    useDataGridScrollRestore(confirmedApiRef);

  const [assignmentsCounts, setAssignmentsCounts] = useState();

  const [listOfServices] = useState([]);
  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);

  // Set up entity tracking for socket room
  useEffect(() => {
    setCurrentEntity({
      entityType: "InterpretationsList",
      entityId: "all",
    });

    return () => {
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const handleContextMenu = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    if (!selectedRow) {
      console.error("No selected row for edit action");
      showSnackbar("Please select an assignment to edit", theme, "error");
      return;
    }

    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        if (!response?.data) {
          throw new Error("No interpretation data received");
        }

        const interpretationData = response.data;

        // Validate required data exists
        if (!interpretationData.id) {
          throw new Error("Invalid interpretation data: missing ID");
        }

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_EDIT,
          Interpretations: listOfServices || [],
          Clients: listOfClients || [],
          Linguists: listOfLinguists || [],
          Languages: listOfLanguages || [],
          InterpretationStatuses: listOfStatuses || [],
          InterpretationTypes: listOfTypes || [],
        };

        setOverlayData(secondData);
        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);
        setContextMenu(null);
      })
      .catch((error) => {
        console.error("Error fetching interpretation for edit:", error);
        const message =
          error.response?.data?.message || error.message || "Failed to load interpretation details.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuAddTask = () => {
    if (!selectedRow) {
      console.error("No selected row for add task action");
      showSnackbar("Please select an assignment to add a task", theme, "error");
      return;
    }

    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    if (!interpretationData) {
      console.error("No interpretation data found for selected row:", selectedRow);
      showSnackbar("Assignment details not found. Please try again.", theme, "error");
      return;
    }

    // Validate required fields
    if (!interpretationData.id || !interpretationData.ProjectNumber) {
      console.error("Invalid interpretation data:", interpretationData);
      showSnackbar("Invalid assignment data. Please try again.", theme, "error");
      return;
    }

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow,
        ServiceType: "Interpretation",
      },
      Service: {
        id: interpretationData.id,
        ProjectNumber: interpretationData.ProjectNumber,
        Details: interpretationData.Details || "",
        ServiceCategory: interpretationData.ServiceCategory || "Interpretation",
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuComplete = () => {
    api
      .get(`/services/interpretations/${selectedRow}`)
      .then((response) => {
        const interpretationData = response.data;

        const secondData = {
          ...interpretationData,
          Action: OVERLAY_NAMES.INTERPRETATION_ACTION_COMPLETE,
          Interpretations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          InterpretationStatuses: listOfStatuses,
          InterpretationTypes: listOfTypes,
        };

        setOverlayData(secondData);

        setOverlayName(OVERLAY_NAMES.INTERPRETATION_NEW_EDIT);

        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuCallRequestor = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    const requestorPhones = interpretationData?.Requestor?.PhoneNumbers || [];
    const requestorPrimary = requestorPhones.find((p) => p.IsPrimary) || requestorPhones[0];
    const requestorPhone = requestorPrimary?.Number || interpretationData?.Requestor?.PhoneNumber;
    if (requestorPhone) {
      window.location.href = `tel:${requestorPhone.replace(/[^0-9+]/g, "")}`;
    }
    setContextMenu(null);
  };

  const handleContextMenuEmailRequestor = () => {
    const interpretationData = listOfServices.find(
      ({ id }) => id === selectedRow,
    );

    if (interpretationData?.Requestor?.Email) {
      const email = interpretationData.Requestor.Email;
      const subject =
        "Regarding Assignment N° " + interpretationData.ProjectNumber;
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
        subject,
      )}`;
      window.open(mailtoLink, "_blank");
    }
    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  // Column definitions for filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (_, row) => row.ProjectNumber || "N/A",
      },
      {
        field: "Date",
        valueGetter: (_, row) => (row.Date ? formatDateLong(row.Date) : "N/A"),
      },
      {
        field: "InterpretationStatus",
        valueGetter: (_, row) => row.InterpretationStatus || "N/A",
      },
      {
        field: "Client",
        valueGetter: (_, row) => row.Client?.Account || "N/A",
      },
      {
        field: "Requestor",
        valueGetter: (_, row) => row.Requestor?.Name || "N/A",
      },
      {
        field: "TimeStart",
        valueGetter: (_, row) => {
          const data = row.TimeStart;
          if (!data || data === "00:00:00") return "N/A";
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        },
      },
      {
        field: "TimeEnd",
        valueGetter: (_, row) => {
          const data = row.TimeEnd;
          if (!data || data === "00:00:00") return "N/A";
          return dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        },
      },
      {
        field: "Duration",
        valueGetter: (_, row) => {
          const data = row.Duration;
          if (!data || data === "00:00:00") return "N/A";
          const [hours, minutes] = data.split(":");
          return `${hours}:${minutes}`;
        },
      },
      {
        field: "Linguist",
        valueGetter: (_, row) => row.Linguist?.Name || "N/A",
      },
      { field: "Details", valueGetter: (_, row) => row.Details || "N/A" },
    ],
    [],
  );

  const {
    columnFilters: requestedFilters,
    filteredData: requestedFilteredData,
    handleFilterChange: handleRequestedFilterChange,
    clearAllFilters: clearRequestedFilters,
    hasActiveFilters: requestedHasActiveFilters,
  } = useColumnFilters(requestedAssignments || [], filterColumns);

  // Cross-filtering data calculation for requested
  const requestedFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(requestedFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = requestedAssignments || [];
      } else {
        filteredData = (requestedAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [requestedFilters, requestedAssignments, filterColumns]);

  const {
    columnFilters: confirmedFilters,
    filteredData: confirmedFilteredData,
    handleFilterChange: handleConfirmedFilterChange,
    clearAllFilters: clearConfirmedFilters,
    hasActiveFilters: confirmedHasActiveFilters,
  } = useColumnFilters(confirmedAssignments || [], filterColumns);

  // Cross-filtering data calculation for confirmed
  const confirmedFilterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(confirmedFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = confirmedAssignments || [];
      } else {
        filteredData = (confirmedAssignments || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(null, row);
            return selectedValues.includes(value);
          });
        });
      }

      return filteredData;
    });
  }, [confirmedFilters, confirmedAssignments, filterColumns]);

  const [openRequestedFilter, setOpenRequestedFilter] = useState(null);
  const [openConfirmedFilter, setOpenConfirmedFilter] = useState(null);
  const [requestedFilterModel, setRequestedFilterModel] = useState({
    items: [],
  });
  const [confirmedFilterModel, setConfirmedFilterModel] = useState({
    items: [],
  });

  // Wrapper functions to close all other filters when one opens
  const handleSetOpenRequestedFilter = (value) => {
    if (value) {
      setOpenConfirmedFilter(null);
    }
    setOpenRequestedFilter(value);
  };

  const handleSetOpenConfirmedFilter = (value) => {
    if (value) {
      setOpenRequestedFilter(null);
    }
    setOpenConfirmedFilter(value);
  };

  // Wrapped clear handlers that also clear quick filters
  const handleClearRequestedFilters = () => {
    clearRequestedFilters();
    setRequestedFilterModel({ items: [] });
  };

  const handleClearConfirmedFilters = () => {
    clearConfirmedFilters();
    setConfirmedFilterModel({ items: [] });
  };

  // Check for quick filters on requested
  const requestedHasQuickFilter =
    requestedFilterModel?.quickFilterValues?.length > 0;
  const requestedHasAnyActiveFilters =
    requestedHasActiveFilters || requestedHasQuickFilter;

  // Check for quick filters on confirmed
  const confirmedHasQuickFilter =
    confirmedFilterModel?.quickFilterValues?.length > 0;
  const confirmedHasAnyActiveFilters =
    confirmedHasActiveFilters || confirmedHasQuickFilter;

  // Fetch monthly interpretation data
  const fetchMonthlyData = useCallback(() => {
    // Validate required parameters
    if (!monthNumbers || monthNumbers.length === 0 || !year) {
      console.error("Invalid parameters for fetchMonthlyData:", { monthNumbers, year });
      handleErrorDialog("Invalid date parameters. Please try again.", theme);
      setIsLoading(false);
      return;
    }

    // Create a simple params object with month and year
    const params = {
      month: monthNumbers[0], // Just use the first month for now
      year: year,
    };

    setIsLoading(true);
    captureRequestedScroll();
    captureConfirmedScroll();

    api
      .all([
        api.get("/dashboard/interpretations/monthly/requested", {
          params: params,
        }),
        api.get("/dashboard/interpretations/monthly/confirmed", {
          params: params,
        }),
        api.get("/dashboard/interpretations/monthly/counts", {
          params: params,
        }),
      ])
      .then(
        api.spread((requestedResponse, confirmedResponse, countsResponse) => {
          // Validate and set data with null checks
          try {
            if (requestedResponse?.data) {
              setRequestedAssignments(requestedResponse.data);
              restoreRequestedScroll(requestedResponse.data);
            } else {
              setRequestedAssignments([]);
              console.warn("No requested assignments data received");
            }
            
            if (confirmedResponse?.data) {
              setConfirmedAssignments(confirmedResponse.data);
              restoreConfirmedScroll(confirmedResponse.data);
            } else {
              setConfirmedAssignments([]);
              console.warn("No confirmed assignments data received");
            }
            
            if (countsResponse?.data) {
              setAssignmentsCounts(countsResponse.data);
            } else {
              setAssignmentsCounts({});
              console.warn("No counts data received");
            }
          } catch (error) {
            console.error("Error processing API response:", error);
            handleErrorDialog("Error processing data. Please try again.", theme);
          } finally {
            setIsLoading(false);
          }
        }),
      )
      .catch((error) => {
        console.error("Monthly dashboard API error:", error);
        const message =
          error.response?.data?.message ||
          error.message ||
          "Unable to connect to the server. Please try again later.";
        handleErrorDialog(message, theme);
        setIsLoading(false);
      });
  }, [
    monthNumbers,
    year,
    handleErrorDialog,
    theme,
    captureRequestedScroll,
    restoreRequestedScroll,
    captureConfirmedScroll,
    restoreConfirmedScroll,
  ]);

  useEffect(() => {
    fetchMonthlyData();
  }, [refreshPage, dateSelection, fetchMonthlyData]);

  // Listen for interpretation updates from other members
  const handleInterpretationsMonthlyRefetch = useCallback(
    (updatedBy) => {
      // Store update info for snackbar (separate effect will show it)
      setLastUpdate(updatedBy);

      // Refetch the data
      fetchMonthlyData();
    },
    [fetchMonthlyData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `Assignment #${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Assignment updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  useAssignmentUpdates({
    modelType: "InterpretationsList",
    recordId: "all",
    onUpdate: handleInterpretationsMonthlyRefetch,
  });

  // Fetch static data for context menu
  useEffect(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/interpretations/statuses"),
        api.get("/services/interpretations/types"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
          ) => {
            // Set data with validation and fallbacks
            setListOfClients(clientsResponse?.data || []);
            setListOfLinguists(linguistsResponse?.data || []);
            setListOfLanguages(languagesResponse?.data || []);
            setListOfStatuses(statusesResponse?.data || []);
            setListOfTypes(typesResponse?.data || []);
          
          },
        ),
      )
      .catch((error) => {
        console.error("Error fetching static data:", error);
        const message =
          error.response?.data?.message ||
          error.message ||
          "Failed to load required data. Some features may be limited.";
        handleErrorDialog(message, theme);
        
        // Set empty arrays as fallbacks
        setListOfClients([]);
        setListOfLinguists([]);
        setListOfLanguages([]);
        setListOfStatuses([]);
        setListOfTypes([]);
      });
  }, [handleErrorDialog, theme]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A" && v2 === "N/A") return 0;
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    
    // Try multiple date formats to handle different server responses
    const formats = ["M/D/YYYY", "YYYY-MM-DD", "MM/DD/YYYY", "YYYY/MM/DD"];
    let date1, date2;
    
    for (const format of formats) {
      try {
        date1 = dayjs(v1, format);
        date2 = dayjs(v2, format);
        if (date1.isValid() && date2.isValid()) {
          return date1.valueOf() - date2.valueOf();
        }
      } catch (e) {
        // Continue to next format
      }
    }
    
    // Fallback to string comparison if no valid date format found
    return String(v1).localeCompare(String(v2));
  };

  const getColumns = (
    columnFilters,
    handleFilterChange,
    filterData,
    openFilter,
    setOpenFilter,
  ) => [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 120,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 1.2,
      renderHeader: () => (
        <DataGridHeader
          headerName="Number"
          children={
            <ColumnFilter
              field="ProjectNumber"
              data={filterData[0] || []}
              onFilterChange={(values) =>
                handleFilterChange("ProjectNumber", values)
              }
              selectedValues={columnFilters.ProjectNumber || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? formatDateLong(data) : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 180,
      flex: 1.8,
      renderHeader: () => (
        <DataGridHeader
          headerName="Date"
          children={
            <ColumnDateFilter
              field="Date"
              data={filterData[1] || []}
              onFilterChange={(values) => handleFilterChange("Date", values)}
              selectedValues={columnFilters.Date || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "InterpretationStatus",
      headerName: "Status",
      minWidth: 100,
      flex: 1.0,
      renderHeader: () => (
        <DataGridHeader
          headerName="Status"
          children={
            <ColumnFilter
              field="InterpretationStatus"
              data={filterData[2] || []}
              onFilterChange={(values) =>
                handleFilterChange("InterpretationStatus", values)
              }
              selectedValues={columnFilters.InterpretationStatus || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: () => (
        <DataGridHeader
          headerName="Client"
          children={
            <ColumnFilter
              field="Client"
              data={filterData[3] || []}
              onFilterChange={(values) => handleFilterChange("Client", values)}
              selectedValues={columnFilters.Client || []}
              valueGetter={(_, params) => params.Client?.Account || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Requestor"
          children={
            <ColumnFilter
              field="Requestor"
              data={filterData[4] || []}
              onFilterChange={(values) =>
                handleFilterChange("Requestor", values)
              }
              selectedValues={columnFilters.Requestor || []}
              valueGetter={(_, params) => params.Requestor?.Name || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "TimeStart",
      headerName: "Time Start",
      valueGetter: (_, params) => {
        const data = params.TimeStart;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          value = dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }

        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: () => (
        <DataGridHeader
          headerName="Time Start"
          children={
            <ColumnFilter
              field="TimeStart"
              data={filterData[5] || []}
              onFilterChange={(values) =>
                handleFilterChange("TimeStart", values)
              }
              selectedValues={columnFilters.TimeStart || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "TimeEnd",
      headerName: "Time End",
      valueGetter: (_, params) => {
        const data = params.TimeEnd;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          value = dayjs
            .tz(data, "HH:mm:ss", "America/New_York")
            .format("h:mm A");
        }

        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: () => (
        <DataGridHeader
          headerName="Time End"
          children={
            <ColumnFilter
              field="TimeEnd"
              data={filterData[6] || []}
              onFilterChange={(values) => handleFilterChange("TimeEnd", values)}
              selectedValues={columnFilters.TimeEnd || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Duration",
      headerName: "Duration",
      valueGetter: (_, params) => {
        const data = params.Duration;
        let value = "N/A";

        if (data && data !== "00:00:00") {
          const [hours, minutes] = data.split(":");
          value = `${hours}:${minutes}`;
        }

        return value;
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: () => (
        <DataGridHeader
          headerName="Duration"
          children={
            <ColumnFilter
              field="Duration"
              data={filterData[7] || []}
              onFilterChange={(values) =>
                handleFilterChange("Duration", values)
              }
              selectedValues={columnFilters.Duration || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Linguist"
          children={
            <ColumnFilter
              field="Linguist"
              data={filterData[8] || []}
              onFilterChange={(values) =>
                handleFilterChange("Linguist", values)
              }
              selectedValues={columnFilters.Linguist || []}
              valueGetter={(_, params) => params.Linguist?.Name || "N/A"}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: () => (
        <DataGridHeader
          headerName="Details"
          children={
            <ColumnFilter
              field="Details"
              data={filterData[9] || []}
              onFilterChange={(values) => handleFilterChange("Details", values)}
              selectedValues={columnFilters.Details || []}
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          }
        />
      ),
    },
  ];

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({ hasAnyActiveFilters, onClear, title }) => {
      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {title && (
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.text2,
                margin: 0,
                padding: 0,
                alignSelf: "flex-start",
              }}
            >
              {title}
            </Typography>
          )}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            {hasAnyActiveFilters && (
              <Button
                startIcon={<ClearIcon />}
                onClick={onClear}
                variant="outlined"
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>

          <Box sx={{ flex: 1 }} />

          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <CEQuickFilter />
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [
    theme.palette.background.default,
    theme.palette.primary.buttonHoverSelected,
    theme.palette.primary.main,
    theme.palette.primary.text2,
  ]);

  const getDataGrid = (
    title,
    listOfServices,
    filteredData,
    columnFilters,
    handleFilterChange,
    clearAllFilters,
    hasAnyActiveFilters,
    openFilter,
    setOpenFilter,
    filterData,
    filterModel,
    onFilterModelChange,
    apiRef,
  ) => {
    return (
      <>
        <DataGrid
          loading={isLoading}
          apiRef={apiRef}
          initialState={{
            pagination: { paginationModel: { pageSize: 5 } },
          }}
          pageSizeOptions={[5, 10, 25, { value: -1, label: "All" }]}
          getRowId={(row) => row.id}
          rows={filteredData || []}
          columns={getColumns(
            columnFilters,
            handleFilterChange,
            filterData || [],
            openFilter,
            setOpenFilter,
          )}
          disableColumnSorting
          disableColumnMenu
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          rowSelectionModel={
            openFilter || openRequestedFilter || openConfirmedFilter
              ? []
              : undefined
          }
          disableRowSelectionOnClick={
            !!(openFilter || openRequestedFilter || openConfirmedFilter)
          }
          onRowClick={(params, event, details) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            // Close all other filters
            if (openRequestedFilter) {
              handleSetOpenRequestedFilter(null);
              return;
            }
            if (openConfirmedFilter) {
              handleSetOpenConfirmedFilter(null);
              return;
            }

            //to do
            const data = {
              ComingFrom: "/dashboard",
              Interpretation: params.row,
              InterpretationID: params.row.id,
            };

            setOverlayData(data);

            navigate(`/interpretations/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "✘" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: clearAllFilters,
              title,
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "0px",
            boxShadow: "none",
            height: "100%",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },

            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
            "& .MuiDataGrid-scrollbar": {
              margin: "0 0 0 0",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiTypography-root.MuiTypography-body1": {
              textAlign: "left !important",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-root[aria-label*='Export']":
              {
                display: "none !important",
              },
          }}
        />
      </>
    );
  };

  // Generate title for the month display
  const getMonthsTitle = () => {
    if (monthNumbers.length === 1) {
      return thisMonth.long;
    } else if (monthNumbers.length > 1) {
      return `Multiple Months (${monthNumbers.length})`;
    }
    return "No Month Selected";
  };

  return (
    <Grid container spacing={2} size={{ xs: 12 }}>
      {/* First Row: Three Cards */}
      <Grid container spacing={2} size={{ xs: 12 }} sx={{ px: 1, pt: 1 }}>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Requested"
            content={assignmentsCounts?.requested || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Confirmed"
            content={assignmentsCounts?.confirmed || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Cancelled"
            content={assignmentsCounts?.cancelled || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Completed"
            content={assignmentsCounts?.completed || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12 / 5 }}>
          <CardDashboardText
            title="Total"
            content={assignmentsCounts?.all || (isLoading ? "" : "0")}
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Second Row: Two Grids with DataGrid components  */}
      <Grid container spacing={2} size={{ xs: 12 }} sx={{ px: 1 }}>
        <Grid size={{ xs: 12 }} sx={{ display: "flex", minHeight: 0 }}>
          <CardDashboardItem
            height="100%"
            title={null}
            content={
              requestedAssignments?.length > 0 || isLoading
                ? getDataGrid(
                    `Requested Assignments - ${getMonthsTitle()} ${year}`,
                    requestedAssignments,
                    requestedFilteredData,
                    requestedFilters,
                    handleRequestedFilterChange,
                    handleClearRequestedFilters,
                    requestedHasAnyActiveFilters,
                    openRequestedFilter,
                    handleSetOpenRequestedFilter,
                    requestedFilterData,
                    requestedFilterModel,
                    setRequestedFilterModel,
                    requestedApiRef,
                  )
                : `No Requested Assignments for ${getMonthsTitle()} ${year}.`
            }
            isLoading={isLoading}
          />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ pb: 1, display: "flex", minHeight: 0 }}>
          <CardDashboardItem
            height="100%"
            title={null}
            content={
              confirmedAssignments?.length > 0 || isLoading
                ? getDataGrid(
                    `Confirmed Assignments - ${getMonthsTitle()} ${year}`,
                    confirmedAssignments,
                    confirmedFilteredData,
                    confirmedFilters,
                    handleConfirmedFilterChange,
                    handleClearConfirmedFilters,
                    confirmedHasAnyActiveFilters,
                    openConfirmedFilter,
                    handleSetOpenConfirmedFilter,
                    confirmedFilterData,
                    confirmedFilterModel,
                    setConfirmedFilterModel,
                    confirmedApiRef,
                  )
                : `No Confirmed Assignments for ${getMonthsTitle()} ${year}.`
            }
            isLoading={isLoading}
          />
        </Grid>
      </Grid>

      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem onClick={handleContextMenuEdit}>
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Edit
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuComplete}>
          <ListItemIcon>
            <TaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Complete
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuAddTask}>
          <ListItemIcon>
            <AddTaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Add Task
          </ListItemText>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={handleContextMenuCallRequestor}>
          <ListItemIcon>
            <CallRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Call{" "}
            {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
              ?.Name || "Requestor"}
          </ListItemText>
        </MenuItem>

        <MenuItem onClick={handleContextMenuEmailRequestor}>
          <ListItemIcon>
            <EmailRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Email{" "}
            {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
              ?.Name || "Requestor"}
          </ListItemText>
        </MenuItem>
      </Menu>
    </Grid>
  );
};

export default DashboardInterpretationsMonthly;
